import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Reusable skeleton loader component for better perceived performance
 */
@Component({
  selector: 'app-skeleton-loader',
  standalone: true,
  imports: [CommonModule],
  host: { class: 'block' },
  template: `
    <div class="animate-pulse" [class]="containerClass()">
      @if (variant() === 'card') {
      <!-- Card Skeleton -->
      <div class="bg-white rounded-lg shadow p-6">
        <div class="h-6 bg-muted rounded w-1/3 mb-4"></div>
        <div class="space-y-3">
          <div class="h-4 bg-muted rounded"></div>
          <div class="h-4 bg-muted rounded w-5/6"></div>
          <div class="h-4 bg-muted rounded w-4/6"></div>
        </div>
      </div>
      } @else if (variant() === 'header') {
      <!-- Header Skeleton -->
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between mb-4">
          <div class="h-8 bg-muted rounded w-1/3"></div>
          <div class="h-6 bg-muted rounded-full w-24"></div>
        </div>
        <div class="grid grid-cols-3 gap-4">
          <div class="h-4 bg-muted rounded"></div>
          <div class="h-4 bg-muted rounded"></div>
          <div class="h-4 bg-muted rounded"></div>
        </div>
      </div>
      } @else if (variant() === 'timeline') {
      <!-- Timeline Skeleton -->
      <div class="bg-white rounded-lg shadow p-6">
        <div class="h-6 bg-muted rounded w-1/4 mb-4"></div>
        <div class="flex justify-between items-center">
          <div class="flex flex-col items-center gap-2">
            <div class="h-14 w-14 bg-muted rounded-full"></div>
            <div class="h-3 bg-muted rounded w-20"></div>
          </div>
          <div class="flex-1 h-0.5 bg-muted mx-2"></div>
          <div class="flex flex-col items-center gap-2">
            <div class="h-14 w-14 bg-muted rounded-full"></div>
            <div class="h-3 bg-muted rounded w-20"></div>
          </div>
          <div class="flex-1 h-0.5 bg-muted mx-2"></div>
          <div class="flex flex-col items-center gap-2">
            <div class="h-14 w-14 bg-muted rounded-full"></div>
            <div class="h-3 bg-muted rounded w-20"></div>
          </div>
          <div class="flex-1 h-0.5 bg-muted mx-2"></div>
          <div class="flex flex-col items-center gap-2">
            <div class="h-14 w-14 bg-muted rounded-full"></div>
            <div class="h-3 bg-muted rounded w-20"></div>
          </div>
          <div class="flex-1 h-0.5 bg-muted mx-2"></div>
          <div class="flex flex-col items-center gap-2">
            <div class="h-14 w-14 bg-muted rounded-full"></div>
            <div class="h-3 bg-muted rounded w-20"></div>
          </div>
        </div>
      </div>
      } @else if (variant() === 'actions') {
      <!-- Actions Sidebar Skeleton -->
      <div class="bg-white rounded-lg shadow p-6">
        <div class="h-6 bg-muted rounded w-2/3 mb-4"></div>
        <div class="space-y-3">
          <div class="h-10 bg-muted rounded"></div>
          <div class="h-10 bg-muted rounded"></div>
          <div class="h-10 bg-muted rounded"></div>
        </div>
      </div>
      } @else if (variant() === 'full') {
      <!-- Full Page Skeleton (Analysis Detail) -->
      <div class="space-y-6">
        <!-- Header -->
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="h-8 bg-muted rounded w-1/3"></div>
            <div class="h-6 bg-muted rounded-full w-24"></div>
          </div>
          <div class="grid grid-cols-3 gap-4">
            <div class="h-4 bg-muted rounded"></div>
            <div class="h-4 bg-muted rounded"></div>
            <div class="h-4 bg-muted rounded"></div>
          </div>
        </div>

        <!-- Timeline -->
        <div class="bg-white rounded-lg shadow p-6">
          <div class="h-6 bg-muted rounded w-1/4 mb-4"></div>
          <div class="flex justify-between items-center">
            <div class="flex flex-col items-center gap-2">
              <div class="h-14 w-14 bg-muted rounded-full"></div>
              <div class="h-3 bg-muted rounded w-20"></div>
            </div>
            <div class="flex-1 h-0.5 bg-muted mx-2"></div>
            <div class="flex flex-col items-center gap-2">
              <div class="h-14 w-14 bg-muted rounded-full"></div>
              <div class="h-3 bg-muted rounded w-20"></div>
            </div>
            <div class="flex-1 h-0.5 bg-muted mx-2"></div>
            <div class="flex flex-col items-center gap-2">
              <div class="h-14 w-14 bg-muted rounded-full"></div>
              <div class="h-3 bg-muted rounded w-20"></div>
            </div>
            <div class="flex-1 h-0.5 bg-muted mx-2"></div>
            <div class="flex flex-col items-center gap-2">
              <div class="h-14 w-14 bg-muted rounded-full"></div>
              <div class="h-3 bg-muted rounded w-20"></div>
            </div>
            <div class="flex-1 h-0.5 bg-muted mx-2"></div>
            <div class="flex flex-col items-center gap-2">
              <div class="h-14 w-14 bg-muted rounded-full"></div>
              <div class="h-3 bg-muted rounded w-20"></div>
            </div>
          </div>
        </div>

        <!-- Content Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Main Content (2/3) -->
          <div class="lg:col-span-2">
            <div class="bg-white rounded-lg shadow p-6">
              <div class="h-6 bg-muted rounded w-1/3 mb-4"></div>
              <div class="space-y-3">
                <div class="h-4 bg-muted rounded"></div>
                <div class="h-4 bg-muted rounded w-5/6"></div>
                <div class="h-4 bg-muted rounded w-4/6"></div>
                <div class="h-4 bg-muted rounded"></div>
                <div class="h-4 bg-muted rounded w-3/4"></div>
              </div>
            </div>
          </div>

          <!-- Sidebar (1/3) -->
          <div class="lg:col-span-1">
            <div class="bg-white rounded-lg shadow p-6">
              <div class="h-6 bg-muted rounded w-2/3 mb-4"></div>
              <div class="space-y-3">
                <div class="h-10 bg-muted rounded"></div>
                <div class="h-10 bg-muted rounded"></div>
                <div class="h-10 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      } @else {
      <!-- Default Line Skeleton -->
      <div class="space-y-3">
        <div class="h-4 bg-muted rounded"></div>
        <div class="h-4 bg-muted rounded w-5/6"></div>
        <div class="h-4 bg-muted rounded w-4/6"></div>
      </div>
      }
    </div>
  `,
})
export class SkeletonLoader {
  variant = input<
    'card' | 'header' | 'timeline' | 'actions' | 'full' | 'lines'
  >('lines');
  lines = input<number>(3);
  containerClass = input<string>('');
}
